/**
 * Registro de uso do painel (decisões 164 a 172; docs/telemetria.md do backend; análise em
 * conhecimento/analises/ANALISE-TELEMETRIA.md).
 *
 * `emitActivity(nome, props, obj)` monta o envelope e grava no pool; nada vai para a rede na
 * hora. Regras que valem aqui:
 * - só registra com alguém logado; a identidade que vale é a do servidor (a v2 confere);
 * - nada de dados pessoais nas props: contatos dos leads, texto digitado, email, termos de busca
 *   (só o servidor manda os termos, no `search.started`);
 * - a rota vai como modelo (`/list/:module/:id/leads`), e os ids vão em `obj`;
 * - nunca lança: o registro de uso não pode quebrar uma tela.
 */
import { getBrand } from '../../config/brand';
import { DEBUG } from '../../config/env';
import { readAuth } from '../auth';
import { getStoredFingerprint } from '../fingerprint';
import { getRequestContext } from '../requestContext';
import { activitySpec, type ActivityEventName, type ActivitySurface } from './catalog';
import { createDefaultOutbox, type ActivityEnvelope, type Outbox } from './outbox';
import { ActivityPool } from './pool';
import { sendActivityBatch } from './sender';
import { newId, nextTabSeq, startActiveTimer, touchSession, type ActiveTimer } from './session';

export type { ActivityEventName } from './catalog';

export interface ActivityObject {
  search?: string;
  run?: string;
  step?: number;
  history?: string;
  job?: string;
  place?: string;
  item?: string;
  suggestion?: string;
}

export interface EmitOptions {
  /** Sinal da extensão repassado pelo painel (decisão 169). */
  surface?: ActivitySurface;
  /** Quando aconteceu, se não foi agora (sinal guardado pela extensão com o painel fechado). */
  at?: string;
  /** Só em `error.client` e `extension.error`: o servidor limpa e agrupa. */
  error?: { message?: string; stack?: string };
  /** Id estável vindo da origem (sinal da extensão): recolhido duas vezes, conta uma. */
  eventId?: string;
}

type Props = Record<string, unknown>;

const DEVICE_ID = /^[A-Za-z0-9_-]{8,128}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJECT_ID = /^[a-f0-9]{24}$/i;
const TOKEN = /^[A-Za-z0-9_.:-]{1,128}$/;

let currentRoute: string | null = null;
let pool: ActivityPool | null = null;
let outbox: Outbox | null = null;
let activeTimer: ActiveTimer | null = null;

/**
 * Modelo da rota: sem query string e com os ids trocados (`/list/emails/65f…,65a…/leads` vira
 * `/list/emails/:id/leads`). O roteador informa o modelo exato quando já está montado.
 */
export function routeTemplate(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] || '/';
  return (
    path
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        const parts = segment.split(',');
        const idLike = parts.every(
          (part) =>
            OBJECT_ID.test(part) || /^\d{4,}$/.test(part) || /^[0-9a-f-]{32,36}$/i.test(part),
        );
        return idLike ? ':id' : segment;
      })
      .join('/')
      .slice(0, 200) || '/'
  );
}

export function setActivityRoute(template: string): void {
  currentRoute = template.startsWith('/') ? template.slice(0, 200) : `/${template}`.slice(0, 200);
}

function appVersion(): string | undefined {
  try {
    return typeof __APP_VERSION__ === 'string' && __APP_VERSION__
      ? __APP_VERSION__.slice(0, 40)
      : undefined;
  } catch {
    return undefined;
  }
}

function cleanProps(props: Props | undefined): Props {
  const out: Props = {};
  for (const [key, value] of Object.entries(props ?? {})) {
    if (value !== undefined && value !== null) out[key] = value;
  }
  return out;
}

function cleanObject(obj: ActivityObject | undefined): ActivityObject | undefined {
  if (!obj) return undefined;
  const out: ActivityObject = {};
  if (obj.search && OBJECT_ID.test(obj.search)) out.search = obj.search.toLowerCase();
  if (typeof obj.step === 'number' && Number.isInteger(obj.step) && obj.step >= 0)
    out.step = obj.step;
  for (const key of ['run', 'history', 'job', 'place', 'item', 'suggestion'] as const) {
    const value = obj[key];
    if (typeof value === 'string' && TOKEN.test(value)) out[key] = value;
  }
  return Object.keys(out).length ? out : undefined;
}

function ensurePool(): ActivityPool {
  if (pool) return pool;
  outbox = createDefaultOutbox();
  pool = new ActivityPool({
    outbox,
    send: sendActivityBatch,
    getUserId: () => readAuth()?.userId ?? null,
    drainActive: () => activeTimer?.drain() ?? {},
    newId,
    onDropped: (byName, total) => emitActivity('activity.dropped', { total, byName }),
  });
  return pool;
}

/** Registra um evento do catálogo. Fora do catálogo ou sem login, não registra. */
export function emitActivity(
  name: ActivityEventName,
  props?: Props,
  obj?: ActivityObject,
  options: EmitOptions = {},
): void {
  try {
    const surface = options.surface ?? 'panel';
    const spec = activitySpec(name);
    if (!spec || !spec.from.includes(surface)) {
      if (DEBUG) console.warn('[activity] evento fora do catálogo ou da origem', name, surface);
      return;
    }
    const auth = readAuth();
    if (!auth) return;

    const now = Date.now();
    const { tabId, seq } = nextTabSeq();
    const envelope: ActivityEnvelope = {
      eventId:
        options.eventId && UUID.test(options.eventId) ? options.eventId.toLowerCase() : newId(),
      name,
      v: spec.v,
      at:
        options.at && !Number.isNaN(Date.parse(options.at))
          ? options.at
          : new Date(now).toISOString(),
      surface,
      sessionId: touchSession(now),
      tabId,
      seq,
      route:
        currentRoute ?? routeTemplate(typeof location === 'undefined' ? '/' : location.pathname),
    };
    const props_ = cleanProps(props);
    if (Object.keys(props_).length) envelope['props'] = props_;
    const object = cleanObject(obj);
    if (object) envelope['obj'] = object;
    const deviceId = getStoredFingerprint();
    if (deviceId && DEVICE_ID.test(deviceId)) envelope['deviceId'] = deviceId;
    const version = appVersion();
    if (version) envelope['appVersion'] = version;
    const ctx = getRequestContext();
    if (ctx.version) envelope['extVersion'] = String(ctx.version).slice(0, 40);
    const brand = getBrand().source;
    if (brand === 'lead-machine' || brand === 'eminer') envelope['brand'] = brand;
    const lang = ctx.language ? String(ctx.language).slice(0, 20) : undefined;
    let tz: string | undefined;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 60);
    } catch {
      tz = undefined;
    }
    if (lang || tz) envelope['ctx'] = { ...(lang ? { lang } : {}), ...(tz ? { tz } : {}) };
    if (options.error && (name === 'error.client' || name === 'extension.error')) {
      envelope['error'] = {
        message: String(options.error.message ?? '').slice(0, 4000),
        stack: String(options.error.stack ?? '').slice(0, 16000),
      };
    }

    if (DEBUG) console.debug('[activity]', name, envelope);
    void ensurePool().add({
      eventId: envelope.eventId,
      userId: auth.userId,
      priority: spec.priority,
      createdAt: now,
      envelope,
    });
  } catch (err) {
    if (DEBUG) console.warn('[activity] emit falhou', err);
  }
}

/** Envia o pool agora (testes e telas que precisam garantir o envio). */
export function flushActivity(): Promise<void> {
  return ensurePool().flush('manual');
}

let started = false;

/**
 * Liga o pool: tempo ativo, envio ao esconder ou fechar a aba, ao voltar a rede e as sobras de
 * antes. Chamado uma vez no boot.
 */
export function startActivity(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const current = ensurePool();
  activeTimer = startActiveTimer();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void current.flush('hidden');
  });
  window.addEventListener('pagehide', () => current.flushOnExit());
  window.addEventListener('online', () => void current.online());
  // Sobras de antes: depois do boot, para não disputar a rede com a primeira tela.
  setTimeout(() => void current.flush('start'), 5000);
}

export function __resetActivity(): void {
  pool = null;
  outbox = null;
  activeTimer?.stop();
  activeTimer = null;
  currentRoute = null;
  started = false;
}
